import type { NavigationGuardWithThis, RouteLocationNormalized } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useAccountStore } from '@core/stores/account.store';
import { LOGIN } from '@apps/account/routing';
import { availableFeaturesFromStudioId } from '@shared/services/features.service';
import { useAppInsightsLogger } from './plugins/appInsights';
import { useEventBus } from '@vueuse/core';
import { verificationRequiredKey } from '@shared/events';
import { identifier } from './models/base';

const accountVerificationBus = useEventBus(verificationRequiredKey);

const permissionRouteGuard = (permissions: string[]): NavigationGuardWithThis<undefined> =>
  (async (to: RouteLocationNormalized) => {
    const accountStore = useAccountStore();
    await accountStore.initialize();
    const appInsightsLogger = useAppInsightsLogger();
    appInsightsLogger?.authenticate(accountStore.userId);
    if (!accountStore.loggedIn) {
      return { name: LOGIN, query: { ret: to.fullPath } };
    }

    // Check if user has required permissions
    if (!permissions.some((p) => accountStore.permissions.includes(p))) {
      // Redirect to home or another page
      return { name: 'studioadmin-home', params: { studioId: to.params.studioId } };
    }
  }).bind(undefined);

const { error } = useToast();
const featureFlagRouteGuard = (featureFlag: string, routeIfFail: string): NavigationGuardWithThis<undefined> =>
  (async (to: RouteLocationNormalized) => {
    const { studioId, jobId } = to.params;
    const featureIdentifiers = (await availableFeaturesFromStudioId(+studioId)).map((x) => x.identifier);
    if (!featureIdentifiers.includes(featureFlag)) {
      error('Access to Route Denied');
      return { name: routeIfFail, params: { studioId: studioId, jobId: jobId } };
    }
  }).bind(undefined);

const studioVerificationRouteGuard = (routeIfFail: string, homeRouteName: string): NavigationGuardWithThis<undefined> =>
  (async (to: RouteLocationNormalized) => {
    const { useFeaturesStore } = await import('@shared/stores/features.store');
    const featuresStore = useFeaturesStore();

    if (!featuresStore.initialized && to.params.studioId) {
      await featuresStore.initializeWithStudioId(to.params.studioId as unknown as identifier);
    }

    if (!featuresStore.hasFeature('studioVerification')) {
      return;
    }

    if (to.name === homeRouteName) {
      return;
    }

    const { useStudioAdminStudioStore } = await import('@apps/studioadmin/stores/studioAdminStudio.store');
    const studioStore = useStudioAdminStudioStore();
    const { studioId } = to.params;

    // Ensure studio details are loaded before checking verification
    await studioStore.initialize(studioId as unknown as identifier);
    const studioDetails = studioStore.studioDetails;
    if (!studioDetails?.isMillersVerified) {
      error("Your studio needs to be verified by Miller's to access this feature");
      accountVerificationBus.emit();
      return { name: routeIfFail, params: { studioId: studioId } };
    }
  }).bind(undefined);

// Cache for SendGrid readiness checks per studio to avoid redundant API calls
type SendGridStatus = 'no-subuser' | 'not-ready' | 'ready';
const sendGridStatusCache = new Map<string, SendGridStatus>();

/** Clears the cached SendGrid status for a studio so the next navigation re-checks. */
const clearSendGridStatusCache = (studioId: string | number) => {
  sendGridStatusCache.delete(String(studioId));
};

const sendGridSetupRouteGuard = (setupRouteName: string, settingsRouteName: string): NavigationGuardWithThis<undefined> =>
  (async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
    const { studioId } = to.params;
    const studioIdStr = String(studioId);

    // Check cache first
    const cached = sendGridStatusCache.get(studioIdStr);
    if (cached === 'ready') {
      next();
      return;
    }

    try {
      const { authedAxios } = await import('@shared/axios');
      const response = await authedAxios.get<boolean>(`/api/studioadmin/studios/${studioId}/sendgridsubuser/status`);
      const hasSubuser = response.data;

      if (!hasSubuser) {
        sendGridStatusCache.set(studioIdStr, 'no-subuser');
        error('SendGrid account setup is required to use marketing campaigns');
        next({ name: setupRouteName, params: { studioId } });
        return;
      }

      // Subuser exists — check if fully configured
      const configResponse = await authedAxios.get<{ isReadyToSend: boolean; asmGroupId: number | null }>(
        `/api/studioadmin/studios/${studioId}/sendgridsubuser/config`,
      );

      // Ensure the per-studio ASM unsubscribe group exists. Fire-and-forget — don't block
      // navigation on a transient SendGrid API failure. The call is idempotent so repeated
      // invocations are safe; the cache prevents this running on every navigation.
      if (!configResponse.data.asmGroupId) {
        authedAxios
          .post(`/api/studioadmin/studios/${studioId}/sendgridsubuser/asm-group/ensure`)
          .catch(() => { /* non-fatal */ });
      }

      if (!configResponse.data.isReadyToSend) {
        sendGridStatusCache.set(studioIdStr, 'not-ready');
        error('Complete your SendGrid sender configuration before using marketing campaigns');
        next({ name: settingsRouteName, params: { studioId } });
        return;
      }

      sendGridStatusCache.set(studioIdStr, 'ready');


    } catch {
      // If the check fails, allow navigation rather than blocking
    }

    next();
  }).bind(undefined);

export { permissionRouteGuard, featureFlagRouteGuard, studioVerificationRouteGuard, sendGridSetupRouteGuard, clearSendGridStatusCache };
